import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import api from "../services/api";
import "./AdminShiftBulkImport.css";

/**
 * シフト表（マトリクス）一括取込
 *  - 行 = 社員（1列目に社員コード S002 等）
 *  - 列 = 日付（1行目に 6/1/2026 等）
 *  - セル = シフトコード（A, B', C, 休, 1 など。空欄は割当なし）
 * セルのコードを管理画面のシフト（ShiftPreset）に対応づけてから登録する。
 * 登録は (社員, 日付) を upsert（表に無い日は変更しない）。
 */

const pad2 = (n) => String(n).padStart(2, "0");
const dateToYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// ヘッダーセル（日付）を YYYY-MM-DD に変換。Date / "6/1/2026" / "2026/6/1" / "2026-06-01" に対応。
const cellToYmd = (v) => {
  if (v instanceof Date && !isNaN(v)) return dateToYmd(v);
  const s = String(v == null ? "" : v).trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/); // 2026-6-1 / 2026/6/1
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/); // 6/1/2026
  if (m) return `${m[3]}-${pad2(m[1])}-${pad2(m[2])}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return dateToYmd(d);
  return null;
};

const norm = (s) => String(s || "").trim().toLowerCase().replace(/[\s　]+/g, "");

const AdminShiftRosterImport = () => {
  const [presets, setPresets] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [dateCols, setDateCols] = useState([]); // [{ col, date }]
  const [rows, setRows] = useState([]); // [{ code, cells: [{date, raw}] }]
  const [codeMapping, setCodeMapping] = useState({}); // { rawCode: presetId('' = 割当てない) }
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/api/shifts/presets/")
      .then((res) => setPresets(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPresets([]));
  }, []);

  const distinctCodes = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.cells.forEach((c) => set.add(c.raw)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ja"));
  }, [rows]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParseError("");
    setResult(null);
    setError("");
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (!grid.length) {
          setParseError("シートにデータがありません。");
          return;
        }

        // 1行目（ヘッダー）から日付列を検出
        const header = grid[0] || [];
        const detectedDateCols = [];
        for (let j = 1; j < header.length; j++) {
          const ymd = cellToYmd(header[j]);
          if (ymd) detectedDateCols.push({ col: j, date: ymd });
        }
        if (detectedDateCols.length === 0) {
          setParseError("1行目から日付の列を検出できませんでした（例: 6/1/2026 や 2026-06-01）。");
          return;
        }

        // 2行目以降：1列目に社員コードがある行のみ対象（空欄行はスキップ）
        const parsedRows = [];
        const codeSet = new Set();
        for (let i = 1; i < grid.length; i++) {
          const row = grid[i] || [];
          const code = String(row[0] == null ? "" : row[0]).trim();
          if (!code) continue;
          const cells = [];
          detectedDateCols.forEach(({ col, date }) => {
            const raw = String(row[col] == null ? "" : row[col]).trim();
            if (raw) {
              cells.push({ date, raw });
              codeSet.add(raw);
            }
          });
          parsedRows.push({ code, cells });
        }
        if (parsedRows.length === 0) {
          setParseError("社員コードの行が見つかりませんでした（1列目に S002 等のコードが必要です）。");
          return;
        }

        setDateCols(detectedDateCols);
        setRows(parsedRows);

        // セルコード → シフトプリセットの自動対応づけ（コードが一致すれば自動選択）
        const initial = {};
        Array.from(codeSet).forEach((raw) => {
          const hit = presets.find((p) => norm(p.code) === norm(raw) || norm(p.name) === norm(raw));
          initial[raw] = hit ? String(hit.id) : "";
        });
        setCodeMapping(initial);
      } catch (err) {
        console.error(err);
        setParseError("Excelの読み込みに失敗しました。ファイル形式を確認してください。");
      }
    };
    reader.readAsBinaryString(file);
  };

  const unmappedCount = distinctCodes.filter((c) => !codeMapping[c]).length;
  const totalCells = rows.reduce((sum, r) => sum + r.cells.length, 0);

  const handleImport = async () => {
    if (rows.length === 0) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const assignments = [];
      rows.forEach(({ code, cells }) => {
        cells.forEach(({ date, raw }) => {
          const pid = codeMapping[raw];
          if (pid) {
            assignments.push({ memberId: code, date, shiftPresetId: Number(pid) });
          }
        });
      });
      if (assignments.length === 0) {
        setError("対応づけ済みのシフトがありません。セルコードをシフトに対応づけてください。");
        setLoading(false);
        return;
      }
      const res = await api.post("/api/shifts/roster-import/", { assignments });
      setResult(res.data);
    } catch (err) {
      const data = err.response?.data;
      setError(data?.detail || data?.message || err.message || "取込に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const dateRangeLabel =
    dateCols.length > 0 ? `${dateCols[0].date} 〜 ${dateCols[dateCols.length - 1].date}` : "";

  return (
    <div className="shift-import-page">
      <Link to="/admin/workstime/schedule" className="shift-import-back">← スケージュールに戻る</Link>

      <header className="shift-import-header">
        <h2 className="shift-import-title">シフト表（一覧形式）から一括登録</h2>
        <p className="shift-import-subtitle">
          Excel（.xlsx）の<strong>1行目に日付</strong>（例: 6/1/2026）、<strong>1列目に社員コード</strong>（例: S002）、
          各セルにシフトコードが入った表をそのまま取り込めます。セルのコードを管理画面のシフトに対応づけてから登録します。
          空欄のセルは「割当なし」として扱い、表に無い日は変更しません。
        </p>
      </header>

      <div className="shift-import-panel">
        <h3 className="shift-import-panel-title">① シフト表ファイルを選択</h3>
        <input type="file" accept=".xlsx,.xls" onChange={handleFile} />
        {fileName && <span style={{ marginLeft: 10, color: "#475569" }}>{fileName}</span>}
        {parseError && (
          <div className="shift-import-alert is-error" role="alert" style={{ marginTop: 10 }}>
            {parseError}
          </div>
        )}
        {rows.length > 0 && (
          <p style={{ marginTop: 10, color: "#334155", fontSize: 14 }}>
            社員 <strong>{rows.length}</strong> 名 ／ 日付 <strong>{dateCols.length}</strong> 日（{dateRangeLabel}） ／
            シフト記入セル <strong>{totalCells}</strong> 件 を検出しました。
          </p>
        )}
      </div>

      {distinctCodes.length > 0 && (
        <div className="shift-import-panel">
          <h3 className="shift-import-panel-title">② セルのコードをシフトに対応づけ</h3>
          <p style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>
            表に出てきたコードごとに、登録するシフトを選んでください。「（割当てない）」のままのコードは取り込みません。
          </p>
          <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 560 }}>
            <thead>
              <tr style={{ background: "#1e3a5f", color: "#fff" }}>
                <th style={{ padding: "8px 10px", textAlign: "left" }}>表のコード</th>
                <th style={{ padding: "8px 10px", textAlign: "left" }}>登録するシフト</th>
              </tr>
            </thead>
            <tbody>
              {distinctCodes.map((raw) => (
                <tr key={raw} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "6px 10px", fontWeight: 600 }}>{raw}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <select
                      value={codeMapping[raw] || ""}
                      onChange={(e) => setCodeMapping((prev) => ({ ...prev, [raw]: e.target.value }))}
                      style={{ width: "100%", padding: "6px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                    >
                      <option value="">（割当てない）</option>
                      {presets.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.code}（{p.name}）{p.is_day_off ? " ※休" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {unmappedCount > 0 && (
            <p style={{ marginTop: 10, color: "#b45309", fontSize: 13 }}>
              ⚠ 未対応づけのコードが {unmappedCount} 件あります。これらのセルは取り込まれません。
            </p>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="shift-import-panel">
          <h3 className="shift-import-panel-title">③ 登録</h3>
          <button type="button" className="shift-import-submit" onClick={handleImport} disabled={loading}>
            {loading ? "登録中…" : "この内容で登録する"}
          </button>

          {error && (
            <div className="shift-import-alert is-error" role="alert" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ marginTop: 16 }}>
              <p className="shift-import-result-message">{result.message}</p>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div className="shift-import-skipped">
                  <strong>登録できなかった項目</strong>
                  <ul>
                    {result.errors.slice(0, 50).map((er, i) => (
                      <li key={i}>{er.message}</li>
                    ))}
                  </ul>
                  {result.error_count > 50 && <p>…他 {result.error_count - 50} 件</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminShiftRosterImport;
